import { AfterViewInit, Component, HostListener, Inject, OnDestroy, PLATFORM_ID, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { isPlatformBrowser } from '@angular/common';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ConnectComponent } from '../../modals/connect/connect.component';
import { EventService } from '../../services/event.service';
import { PopupService } from '../../services/popup.service';
import { WalletConnectService } from '../../services/wallet-connect.service';
import { WINDOW } from '../../services/window';
import { getParamWithoutCookie, SwapStatus } from '../../shared/constants/app-enums';
import { AnalyticsService } from '../../services/analytics.service';
import { getChainData } from '../../shared/constants/wallet-connect.const';
import { NoWalletComponent } from '../../modals/no-wallet/no-wallet.component';
import { ICounter, RaisedInfo } from '../../shared/interfaces/common.interface';
import { balanceData, stakeData, tokenomics } from '../../shared/interfaces/wallet.interface';
import { SwapComponent } from '../swap/swap.component';


@Component({
  selector: 'app-widget',
  templateUrl: './widget.component.html',
  styleUrl: './widget.component.scss',
})
export class Widget implements AfterViewInit, OnDestroy {
  @ViewChild(SwapComponent) swapComp!: SwapComponent;
  private subscriptions: Subscription[] = [];

  isBsNetwork: boolean = false;
  tokenSold = 0;
  maxAmount = 0;
  maxToken = 0;
  minToken = 0;
  usdRaised = 0;
  currentStep = 0;
  userStaked: number = 0;
  userClaimable: number = 0;
  userEthClaimable: number = 0;
  refreshId!: ReturnType<typeof setInterval>;
  refreshRate: number = 500;
  dataLoaded: boolean = false;
  salePrice: number = 0.0337;
  liveCounter: ICounter = { _days: 0, _hours: 0, _minutes: 0, _seconds: 0, _diff: 0 };
  swapMode: string = 'native';
  isNative: boolean = true;
  isWert: boolean = false;
  referUrl: string = '';
  SwapStatus = SwapStatus;
  timeInterval!: ReturnType<typeof setInterval>;

  walletAddress!: string;
  currentStoreData!: tokenomics;
  status!: SwapStatus;
  raisedPercent!: string;
  modeName!: string;
  stakeData!: stakeData;
  tokenName = environment.tokenName;
  raisedInfo: RaisedInfo = {
    raisedPercent: 0,
    usdRaised: 0,
    userClaimable: 0,
    userEthClaimable: 0,
    userStaked: 0,
    maxAmount: 0,
  };

  isMobile: boolean = false;
  windowSize: number = 0;

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (isPlatformBrowser(this._platformId)) {
      this.windowSize = window.innerWidth;
      this.isMobile = this.windowSize <= 1024 ? true : false;
    }
  }
  
  constructor(
    @Inject(PLATFORM_ID) private _platformId: Object,
    @Inject(WINDOW) private window: Window,
    private eventService: EventService,
    private translate: TranslateService,
    private popupService: PopupService,
    private modalService: BsModalService,
    private walletConnectService: WalletConnectService,
    private modalRef: BsModalRef,
    private analyticasService: AnalyticsService,
  ) {
    this.eventService.getEvent('ROUND-END').subscribe((param) => {
      this.eventService.resetCounter();
      this.walletConnectService.localStep++;
      this.walletConnectService.getPresalesData();
      setTimeout(() => this.setTimer(), 2000);
    });
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this._platformId)) {
      this.windowSize = window.innerWidth;
      this.isMobile = this.windowSize <= 1024 ? true : false;
      this.refreshId = setInterval(() => this.getBalanceData(), this.refreshRate);
      this.timeInterval = setInterval(() => this.setTimer(), this.refreshRate);
      this.subscriptions.push(
        this.eventService.getCounter().subscribe((val) => {
          this.liveCounter = val;
        }),
      );
    }
  }

  getBalanceData() {
    const balanceData: balanceData = this.walletConnectService.balanceData;
    this.currentStep = balanceData.currentStep;
    this.tokenSold = balanceData.totalTokensSold;
    this.currentStoreData = this.walletConnectService.tokenomics[this.currentStep];
    this.maxAmount = this.currentStoreData?.maxAmount || 0;
    this.maxToken = this.currentStoreData?.maxToken || 0;
    this.minToken = this.currentStoreData?.minToken || 0;
    this.usdRaised = balanceData.usdRaised ?? 0;
    this.userStaked = this.walletConnectService.stakeData.userStaked;
    this.userClaimable = balanceData.userClaimable;
    this.userEthClaimable = balanceData.ethClaimable ?? 0;
    this.referUrl = this.analyticasService.referUrl;
    this.status = this.walletConnectService.swapStatus;
    this.walletAddress = this.walletConnectService.walletAddress;
    this.modeName = getChainData(this.walletConnectService.chainId).purchaseToken;
    this.stakeData = this.walletConnectService.stakeData;
    this.salePrice = this.currentStoreData?.tokenUSDT;
    this.raisedPercent = this.getRaisedPercent();
    this.dataLoaded = true;

    this.raisedInfo = {
      raisedPercent: this.raisedPercent,
      usdRaised: this.usdRaised,
      userClaimable: this.userClaimable,
      userEthClaimable: this.userEthClaimable,
      userStaked: this.userStaked,
      maxAmount: this.maxAmount,
    };
  }

  connectModal(): void {
    this.modalRef = this.modalService.show(ConnectComponent, {
      class: 'modal-min modal-dialog-centered',
      backdrop: 'static',
    });
  }

  openSwap(isNative: boolean = true, isWert: boolean = false): void {
    if (this.swapComp.wertMode) {
      this.popupService
        .messagePopup({
          icon: 'warning',
          desc: this.translate.instant('popup.are_you_sure'),
          confirmBtnTxt: this.translate.instant('misc.confirm'),
          cancelBtnTxt: this.translate.instant('misc.cancel'),
        })
        .subscribe({
          next: (result: boolean) => {
            //  Read more about isConfirmed, isDenied below
            if (result) {
              this.swapComp.wertMode = false;
              this.swapMode = isNative ? 'native' : isWert ? 'card' : 'usdt';
              this.isNative = isNative;
              this.isWert = isWert;
            }
          },
        });
    } else {
      this.swapMode = isNative ? 'native' : isWert ? 'card' : 'usdt';
      this.isNative = isNative;
      this.isWert = isWert;
    }
    setTimeout(() => this.swapComp.max(), 1000);
  }

  changeChain(mode: string): void {
    if (mode == this.modeName) return;
    const chain = mode === 'ETH' ? environment.eth : environment.bsc;

    if (this.modeName == 'ETH') {
      this.popupService
        .messagePopup({
          icon: 'info',
          title: this.translate.instant('popup.are_you_sure'),
          desc: this.translate.instant('popup.change_swap_text', { apy: this.stakeData.apy, mode: mode }),
          confirmBtnTxt: this.translate.instant('popup.buy_with_mode', { mode: mode }),
          cancelBtnTxt: this.translate.instant('misc.cancel'),
        })
        .subscribe({
          next: (result: boolean) => {
            // Read more about isConfirmed, isDenied below
            if (result) {
              if (this.walletAddress) {
                this.walletConnectService.switchNetwork(chain.chainIdInt);
              } else {
                this.walletConnectService.chainId = chain.chainIdInt;
              }
            }
          },
        });
    } else {
      if (this.walletAddress) {
        this.walletConnectService.switchNetwork(chain.chainIdInt);
      } else {
        this.walletConnectService.chainId = chain.chainIdInt;
      }
    }
  }

  getRaisedPercent() {
    let percent = (this.usdRaised / this.maxAmount) * 100;
    percent = percent > 80 ? percent : 80;
    return (percent >= 100 ? 100 : percent) + '%';
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  setTimer() {
    if (this.dataLoaded) {
      if (this.tokenSold && this.tokenSold > 0) {
        if (this.currentStoreData.endDate < Date.parse(new Date().toString()) && this.currentStep < this.walletConnectService.tokenomics.length - 1) {
          this.eventService.setCounter(this.walletConnectService.tokenomics[this.currentStep + 1].endDate);
        } else {
          this.eventService.setCounter(this.currentStoreData.endDate);
        }
        clearInterval(this.timeInterval);
      }
    }
  }

  connectWallet(mode: string): void {
    this.walletConnectService.connectWallet(mode, this.isBsNetwork);
    this.modalRef.hide();
  }

  openBestWallet() {
    this.modalRef.hide();
    setTimeout(() => {
      this.modalRef = this.modalService.show(NoWalletComponent, {
        class: 'modal-lg modal-dialog-centered bw-modal',
        backdrop: 'static',
      });
    }, 500);
  }
}
